<script setup>
  import { useHead } from "@vueuse/head"
  import BasePage from "./BasePage.vue"

  const socialImage = "https://untrackedapps.com/untracked_apps_logo.png"

  useHead({
    title: "Support – Untracked Apps",
    meta: [
      // SEO
      {
        name: "description",
        content:
          "Get support for Untracked Apps. Contact us with questions, feedback, or issues.",
      },
      {
        name: "keywords",
        content:
          "support, help, contact, Untracked Apps, customer service",
      },
      {
        name: "author",
        content: "Untracked Apps",
      },

      // Open Graph
      {
        property: "og:type",
        content: "website",
      },
      {
        property: "og:title",
        content: "Support – Untracked Apps",
      },
      {
        property: "og:description",
        content:
          "Need help? We're here for you. Contact Untracked Apps support.",
      },
      {
        property: "og:image",
        content: socialImage,
      },
      {
        property: "og:url",
        content: "https://untrackedapps.com/support",
      },
      {
        property: "og:site_name",
        content: "Untracked Apps",
      },

      // Twitter Cards
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:title",
        content: "Support – Untracked Apps",
      },
      {
        name: "twitter:description",
        content:
          "Get help with Untracked Apps. We actually read our email.",
      },
      {
        name: "twitter:image",
        content: socialImage,
      },
      {
        name: "twitter:site",
        content: "@UntrackedApps",
      },
    ],
    link: [
      {
        rel: "canonical",
        href: "https://untrackedapps.com/support",
      },
    ],
  })
</script>

<template>
  <BasePage>
    <section
      class="min-h-[100vh] px-6 py-10 relative overflow-hidden max-w-3xl mx-auto"
    >
      <header class="text-center">
        <h1 class="text-4xl md:text-5xl font-bold text-gray-700">
          Support
        </h1>
        <p class="mt-4 mb-8 text-lg">We're here to help</p>
      </header>

      <section class="space-y-6 text-lg leading-relaxed text-left">
        <p>
          Need help with one of our apps? Have a question, suggestion, or found a bug?
          We'd love to hear from you.
        </p>
        <p>
          Untracked Apps is a product brand operated by Dominic Juan Lebron and is not a separate legal entity.
        </p>

        <h2 class="text-xl font-semibold mt-8 mb-2">Get in Touch</h2>
        <p>
          Email us at
          <a href="mailto:support@untrackedapps.com" class="text-blue-600 hover:underline font-semibold">support@untrackedapps.com</a>
        </p>

        <p>
          We actually read our email and respond to every message. Whether it's a
          technical issue, feature request, or just feedback — we want to hear it.
        </p>

        <h2 class="text-xl font-semibold mt-8 mb-2">What to Include</h2>
        <p>
          To help us assist you faster, please include:
        </p>
        <ul class="space-y-2 list-disc list-outside pl-6">
          <li>Which app you're using</li>
          <li>Your device model and iOS/macOS version</li>
          <li>A description of the issue or question</li>
          <li>Screenshots if applicable</li>
        </ul>

        <h2 class="text-xl font-semibold mt-8 mb-2">Response Time</h2>
        <p>
          We typically respond within 24-48 hours during business days.
          We're a small team, but we're committed to providing great support.
        </p>
      </section>
    </section>
  </BasePage>
</template>
